import {
  AiConfigAgentGet,
  AiConfigOrgAgentGet,
  AiConfigOutputDetailOption,
  AiConfigSettingCategoryGet,
} from '@shared/types/ai-config.types';

/** In-memory mock reference only (used when requests go to apiMockUrl). */
export const AI_CONFIG_ORG_BANNED_WORDS_INFO =
  'Words or phrases the AI must never use. Applies to all tenants in this organization.';

export const AI_CONFIG_TENANT_BANNED_WORDS_INFO =
  'Additional banned words for this tenant. Combined with organization banned words at generation time.';

export const AI_CONFIG_GLOBAL_BANNED_WORDS_INFO =
  'Platform-wide terms that AI agents must never include in generated output (email content, subject lines, segment names, insights summaries, etc.). Merged with organization and tenant banned-word lists at generation time; this platform baseline is always applied first.';

export const AI_CONFIG_OUTPUT_DETAIL_OPTIONS: AiConfigOutputDetailOption[] = [
  { key: 'concise', display_name: 'Concise' },
  { key: 'standard', display_name: 'Standard' },
  { key: 'detailed', display_name: 'Detailed' },
];

/**
 * Catalog of all 6 AI agents with GET metadata (not stored overrides).
 * Each agent's `prompt.value` defaults to `null` (unset); `prompt_info`
 * documents what the per-agent prompt controls.
 */
export const AI_CONFIG_AGENT_CATALOG: AiConfigAgentGet[] = [
  {
    key: 'engagement_insights',
    display_name: 'Email Engagement Insights',
    prompt: {
      value: null,
      prompt_info:
        'Instructions applied when generating campaign performance insights and recommendations (e.g. tone, focus areas, terminology to prefer or avoid).',
    },
    output_detail: {
      value: 'standard',
      options: [...AI_CONFIG_OUTPUT_DETAIL_OPTIONS],
      default: 'standard',
      info: 'Controls report verbosity. concise: shorter, higher-priority insights only; standard: current behavior; detailed: fuller analysis with more supporting context. Default: standard',
    },
    temperature: {
      value: 0.1,
      min: 0.0,
      max: 1.0,
      default: 0.1,
      info: 'Controls variability in insight phrasing. Lower values produce more consistent analysis. Default: 0.1',
    },
    max_output_tokens: {
      value: 30000,
      min: 8192,
      max: 65535,
      default: 30000,
      info: 'Maximum length of the AI insights report. Increase if reports are being cut off. Default: 30000',
    },
  },
  {
    key: 'creative',
    display_name: 'Email Creative',
    prompt: {
      value: null,
      prompt_info:
        'Instructions applied when generating creative email recommendations and improved HTML (e.g. brand voice, messaging priorities, content to avoid).',
    },
    output_detail: {
      value: 'standard',
      options: [...AI_CONFIG_OUTPUT_DETAIL_OPTIONS],
      default: 'standard',
      info: 'Controls response verbosity. concise: fewer, shorter bullets and HTML copy; standard: current behavior; detailed: fuller coverage and richer HTML copy. Default: standard',
    },
    temperature: {
      value: 0.1,
      min: 0.0,
      max: 1.0,
      default: 0.1,
      info: 'Controls randomness in creative suggestions. Lower values produce more consistent, focused output; higher values produce more varied results. Default: 0.1',
    },
    max_output_tokens: {
      value: 65500,
      min: 16384,
      max: 65535,
      default: 65500,
      info: 'Maximum length of the AI response (recommendations + improved HTML). Increase for very large email templates. Default: 65500',
    },
  },
  {
    key: 'audience_naming',
    display_name: 'Audience Smart Naming',
    prompt: {
      value: null,
      prompt_info:
        'Instructions applied when generating audience name and description variants (e.g. naming conventions, tone, terms to avoid).',
    },
    output_detail: {
      value: 'standard',
      options: [...AI_CONFIG_OUTPUT_DETAIL_OPTIONS],
      default: 'standard',
      info: 'Controls response verbosity. concise: fewer name variants with shorter descriptions; standard: current behavior; detailed: more variants with fuller context per name. Default: standard',
    },
    temperature: {
      value: 0.3,
      min: 0.0,
      max: 1.0,
      default: 0.3,
      info: 'Controls naming creativity. Slightly higher than other agents to allow varied name suggestions. Default: 0.3',
    },
    max_output_tokens: {
      value: 2048,
      min: 1024,
      max: 8192,
      default: 2048,
      info: 'Maximum length of the naming response (5 name/description variants). Default: 2048',
    },
  },
  {
    key: 'website_analytics_insights',
    display_name: 'Website Analytics Insights',
    prompt: {
      value: null,
      prompt_info:
        'Instructions applied when generating website analytics performance insights (e.g. focus metrics, tone, terminology to prefer or avoid).',
    },
    output_detail: {
      value: 'standard',
      options: [...AI_CONFIG_OUTPUT_DETAIL_OPTIONS],
      default: 'standard',
      info: 'Controls report verbosity. concise: fewer, shorter insights; standard: current behavior; detailed: fuller coverage with more supporting data points. Default: standard',
    },
    temperature: {
      value: 0.1,
      min: 0.0,
      max: 1.0,
      default: 0.1,
      info: 'Controls variability in insight narrative. Lower values produce more consistent analysis. Default: 0.1',
    },
    max_output_tokens: {
      value: 30000,
      min: 8192,
      max: 65535,
      default: 30000,
      info: 'Maximum length of the analytics insights report. Default: 30000',
    },
  },
  {
    key: 'website_analytics_filters',
    display_name: 'Website Analytics Query Parsing',
    prompt: {
      value: null,
      prompt_info:
        'Instructions applied when extracting structured filter JSON from natural language queries (e.g. preferred field names, defaults for ambiguous queries).',
    },
    output_detail: {
      value: 'standard',
      options: [...AI_CONFIG_OUTPUT_DETAIL_OPTIONS],
      default: 'standard',
      info: 'Controls filter extraction verbosity. concise: extract only the most confident filters; standard: current behavior; detailed: include borderline filters with lower confidence where data supports it. Default: standard',
    },
    temperature: {
      value: 0.1,
      min: 0.0,
      max: 1.0,
      default: 0.1,
      info: 'Controls filter extraction consistency. Lower values produce more deterministic JSON output. Default: 0.1',
    },
    max_output_tokens: {
      value: 1024,
      min: 512,
      max: 4096,
      default: 1024,
      info: 'Maximum response length for the structured filter JSON. Default: 1024',
    },
  },
  {
    key: 'criteria_description',
    display_name: 'Export Criteria Description',
    prompt: {
      value: null,
      prompt_info:
        'Instructions applied when generating plain-English descriptions of export criteria (e.g. tone, level of detail, terms to avoid).',
    },
    output_detail: {
      value: 'standard',
      options: [...AI_CONFIG_OUTPUT_DETAIL_OPTIONS],
      default: 'standard',
      info: 'Controls description verbosity. concise: minimal plain-English summary; standard: current behavior; detailed: fuller explanation of each criterion. Default: standard',
    },
    temperature: {
      value: 0.2,
      min: 0.0,
      max: 1.0,
      default: 0.2,
      info: 'Controls consistency of criteria descriptions. Low value keeps output deterministic for a given input. Default: 0.2',
    },
    max_output_tokens: {
      value: 512,
      min: 256,
      max: 2048,
      default: 512,
      info: 'Maximum response length. Criteria descriptions are a single sentence; this rarely needs changing. Default: 512',
    },
  },
];

/**
 * Returns a deep-cloned default agent catalog for FE fallbacks.
 * @returns Cloned catalog agents with default values.
 */
export function cloneAiConfigAgentCatalog(): AiConfigAgentGet[] {
  return AI_CONFIG_AGENT_CATALOG.map(agent => ({
    ...agent,
    prompt: { ...agent.prompt },
    output_detail: { ...agent.output_detail },
    temperature: { ...agent.temperature },
    max_output_tokens: { ...agent.max_output_tokens },
  }));
}

/**
 * Returns a deep-cloned default org-scope agent catalog (prompt only) for FE
 * fallbacks.
 * @returns Cloned catalog agents in the organization-scope (prompt-only) shape.
 */
export function cloneAiConfigOrgAgentCatalog(): AiConfigOrgAgentGet[] {
  return AI_CONFIG_AGENT_CATALOG.map(agent => ({
    key: agent.key,
    display_name: agent.display_name,
    prompt: { ...agent.prompt },
  }));
}

/**
 * Catalog of platform-wide global setting categories (GET metadata + defaults).
 * Used by the in-memory mock only.
 */
export const AI_CONFIG_GLOBAL_SETTING_CATEGORIES: AiConfigSettingCategoryGet[] =
  [
    {
      key: 'campaign_fetch',
      display_name: 'Campaign Fetch',
      info: 'Minimum days between scheduled campaign list pulls per ESP provider.',
      settings: [
        {
          key: 'adestra_fetch_interval_days',
          display_name: 'Adestra',
          value: 1,
          info: 'Minimum days between scheduled Adestra campaign data pulls. Prevents redundant fetches. Default: 1',
          min: 1,
          max: 90,
          default: 1,
        },
        {
          key: 'puresend_fetch_interval_days',
          display_name: 'Puresend',
          value: 1,
          info: 'Minimum days between scheduled Puresend campaign data pulls. Prevents redundant fetches. Default: 1',
          min: 1,
          max: 90,
          default: 1,
        },
        {
          key: 'awi_puresend_fetch_interval_days',
          display_name: 'AWI Puresend',
          value: 1,
          info: 'Minimum days between scheduled AWI Puresend campaign data pulls. Prevents redundant fetches. Default: 1',
          min: 1,
          max: 90,
          default: 1,
        },
        {
          key: 'mailjet_fetch_interval_days',
          display_name: 'Mailjet',
          value: 1,
          info: 'Minimum days between scheduled Mailjet campaign data pulls. Prevents redundant fetches. Default: 1',
          min: 1,
          max: 90,
          default: 1,
        },
      ],
    },
    {
      key: 'campaign_resync',
      display_name: 'Campaign Resync',
      info: 'Minimum days between full campaign re-validation runs per ESP provider.',
      settings: [
        {
          key: 'adestra_resync_interval_days',
          display_name: 'Adestra',
          value: 14,
          info: 'Minimum days between full Adestra contact/campaign re-validation runs. Default: 14',
          min: 1,
          max: 365,
          default: 14,
        },
        {
          key: 'puresend_resync_interval_days',
          display_name: 'Puresend',
          value: 14,
          info: 'Minimum days between full Puresend contact/campaign re-validation runs. Default: 14',
          min: 1,
          max: 365,
          default: 14,
        },
        {
          key: 'awi_puresend_resync_interval_days',
          display_name: 'AWI Puresend',
          value: 14,
          info: 'Minimum days between full AWI Puresend contact/campaign re-validation runs. Default: 14',
          min: 1,
          max: 365,
          default: 14,
        },
        {
          key: 'mailjet_resync_interval_days',
          display_name: 'Mailjet',
          value: 14,
          info: 'Minimum days between full Mailjet contact/campaign re-validation runs. Default: 14',
          min: 1,
          max: 365,
          default: 14,
        },
      ],
    },
    {
      key: 'data',
      display_name: 'Data Management',
      info: 'Platform-wide settings that control how campaign and insights data is stored and reused.',
      settings: [
        {
          key: 'data_staleness_days',
          display_name: 'Data Staleness (days)',
          value: 1,
          info: 'How long cached metrics and insights remain fresh before a re-fetch is triggered. Applies across all report and insights services. Default: 1',
          min: 1,
          max: 90,
          default: 1,
        },
      ],
    },
    {
      key: 'reliability',
      display_name: 'Reliability',
      info: 'Platform-wide settings that control how storage and downstream service calls recover from transient failures.',
      settings: [
        {
          key: 'max_retries',
          display_name: 'Max Retries',
          value: 5,
          info: 'Maximum retry attempts for GCS reads/writes and downstream campaign service calls. Minimum 1. Default: 5',
          min: 1,
          max: 10,
          default: 5,
        },
      ],
    },
  ];

/**
 * Returns a deep-cloned global settings catalog for mock GET responses.
 * @param overrides - Optional flat key→value overrides from the global store.
 * @returns Cloned setting categories with stored values applied.
 */
export function cloneAiConfigGlobalSettingCategories(
  overrides: Record<string, number> = {},
): AiConfigSettingCategoryGet[] {
  return AI_CONFIG_GLOBAL_SETTING_CATEGORIES.map(category => ({
    ...category,
    settings: category.settings.map(setting => ({
      ...setting,
      value:
        overrides[setting.key] !== undefined
          ? overrides[setting.key]
          : setting.value,
    })),
  }));
}
